import type { RunDetailDto } from '@rw/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sheet } from '../../components/Sheet';
import { Button } from '../../components/ui';
import { invalidateCompetitive, qk } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { toast } from '../../stores/toasts';
import { useT } from '../../i18n';

/** The runner deletes one of their runs, e.g. to sync or upload it again. */
export function DeleteRunSheet({ run, open, onClose, onDeleted }: { run: RunDetailDto; open: boolean; onClose: () => void; onDeleted: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => api(`/runs/${run.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      onDeleted();
      qc.removeQueries({ queryKey: qk.run(run.id) });
      void qc.invalidateQueries({ queryKey: qk.runs });
      void qc.invalidateQueries({ queryKey: ['strava'] });
      invalidateCompetitive(qc);
      toast({ kind: 'success', title: t('Run deleted') });
    },
  });
  const again =
    run.source === 'STRAVA'
      ? t('Your Strava runs open next, so you can import it again.')
      : run.source === 'FILE'
        ? t('You can upload the file again.')
        : t('A run recorded in the app cannot be recovered.');

  return (
    <Sheet open={open} onClose={onClose} label={t('Delete this run')}>
      <div className="space-y-4" data-testid="delete-run-sheet">
        <h2 className="font-display text-2xl font-bold">{t('Delete this run?')}</h2>
        {run.status === 'PENDING' ? (
          <p className="text-sm text-ink-300">{t('It was never finished and earned nothing. Deleting it frees its time, so the same session can be uploaded or imported.')}</p>
        ) : (
          <>
            <p className="text-sm text-ink-300">
              {t('Everything it earned is taken back: XP, distance, its place in events, and its sectors, which go back to whoever held them before.')}
            </p>
            <p className="text-sm text-ink-300">{again}</p>
          </>
        )}
        {del.error ? <p className="text-sm text-enemy">{del.error instanceof ApiError ? del.error.message : t('Something went wrong')}</p> : null}
        <Button variant="danger" className="w-full" loading={del.isPending} onClick={() => del.mutate()} data-testid="delete-run-submit">
          {t('Delete run')}
        </Button>
        <Button variant="ghost" className="w-full" onClick={onClose}>
          {t('Cancel')}
        </Button>
      </div>
    </Sheet>
  );
}
